/**
 * Per-user dashboard state, stored in Supabase Postgres (table
 * `user_dashboard_state`, RLS-scoped to `auth.uid()`), read/written directly
 * from the frontend — a deliberate, narrow exception to "Supabase is
 * auth-only" (see CLAUDE.md). Lets a logged-in user's last active dataset,
 * "always use latest scheduled data" preference, dashboard config
 * (tabs/layouts/tile settings), and which named shared config is marked
 * active follow them across browsers/devices, unlike `activeFileStorage.js`'s,
 * `useDashboardConfig.js`'s, and `activeSharedConfigStorage.js`'s per-browser
 * localStorage. `dashboard_config_saved_at` is only a timestamp pointer —
 * the config content itself stays in `config_data/{user_id}.json` on the
 * backend (see `useDashboardConfig.js::syncFromServerIfNewer`).
 * `active_shared_config_id`/`_name` mirror the per-browser "Aktiv" marker
 * (see `activeSharedConfigStorage.js`) so the Config Manager modal shows the
 * same active badge on every device — the config's actual content is a
 * separate, already-synced thing (`dashboard_config_saved_at`).
 *
 * Every call here silently requires an authenticated Supabase session — RLS
 * rejects reads/writes for anonymous requests, so callers should only invoke
 * these once `useAuth()`'s `user` is present.
 */

import { supabase } from '../lib/supabase';

const TABLE = 'user_dashboard_state';

/**
 * Returns the current user's row, or null if they don't have one yet
 * (brand-new user, or not logged in).
 */
export const getUserDashboardState = async () => {
  const { data, error } = await supabase
    .from(TABLE)
    .select('last_active_file_type, last_active_file_set_at, auto_activate_latest_etl_file, dashboard_config_saved_at, active_shared_config_id, active_shared_config_name, workshop_mode')
    .maybeSingle();
  if (error) throw error;
  return data;
};

/**
 * Upserts the given fields onto the current user's row. `user_id` is never
 * sent — the column defaults to auth.uid() server-side, and RLS's `with
 * check` would reject a mismatched value anyway.
 */
export const upsertUserDashboardState = async (partial) => {
  const { error } = await supabase
    .from(TABLE)
    .upsert({ ...partial, updated_at: new Date().toISOString() }, { onConflict: 'user_id' });
  if (error) throw error;
};
